/**
 * Centralized pixel event dispatch for the public Link Tree page.
 *
 * CRITICAL: every event must be scoped to the current artist's pixels only.
 * Within a single browser tab, multiple artists' SDKs can co-exist if the
 * visitor navigates between /u/<slug> routes — fbq accumulates pixel IDs,
 * gtag accumulates GA properties, etc. Without scoping, a `track` call on
 * artist B's page would also fire to artist A's pixel — i.e. analytics
 * leakage between users.
 *
 * Scoped-call API per provider:
 *   - Facebook Pixel:     fbq('trackSingle', PIXEL_ID, EVENT, payload)
 *                         fbq('trackSingleCustom', PIXEL_ID, EVENT, payload)
 *   - Google Analytics:   gtag('event', EVENT, { send_to: GA_ID, ... })
 *   - TikTok Pixel:       ttq.instance(PIXEL_ID).track(EVENT, payload)
 *                         ttq.instance(PIXEL_ID).page()
 *   - Google Tag Manager: each container loaded with a unique dataLayer name
 *                         (gtm.js param &l=). We push to the artist's own
 *                         dataLayer so other artists' containers don't see
 *                         the event.
 */

export type PixelEventType = 'page_view' | 'link_click' | 'social_click';

export interface PixelRef {
  provider: 'fb' | 'tiktok' | 'google' | 'gtm';
  pixel_id: string;
}

export interface PixelEventPayload {
  /** Human-readable name of what was clicked (e.g. "Listen on Spotify"). */
  label?: string;
  /** Destination URL. */
  url?: string;
  /** Platform key for socials ("spotify", "instagram", etc.). */
  platform?: string;
  /** Block id from the database, useful for downstream attribution. */
  blockId?: string;
  /** Page id — pushed into GTM dataLayer events so artists can filter their
   *  tags to only fire for the page that hosts their pixel. */
  pageId?: string;
  /** Optional monetary value (e.g. for merch). */
  value?: number;
  /** Optional category override. Defaults to "outbound" for link/social. */
  category?: string;
}

function safeCall(fn: () => void, label: string) {
  try { fn(); } catch (e) {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[linktree] pixel event '${label}' failed`, e);
    }
  }
}

/** Sanitized dataLayer variable name for a GTM container. GTM accepts any
 *  JS identifier — we hash the pixel id into a stable, alphanumeric suffix. */
export function gtmDataLayerName(pixelId: string): string {
  return `dataLayer_${pixelId.replace(/[^A-Za-z0-9]/g, '_')}`;
}

/**
 * Fire a pixel event scoped to a specific set of pixels (the current artist's
 * pixels). Other artists' SDKs that happen to be loaded in the same tab from
 * earlier navigation will NOT receive the event.
 */
export function firePixelEvents(
  eventType: PixelEventType,
  pixels: PixelRef[] | null | undefined,
  payload: PixelEventPayload = {},
) {
  if (!pixels || pixels.length === 0) return;
  const w = window as any;
  const label = payload.label || payload.platform || payload.url || 'link';
  const category = payload.category || (eventType === 'social_click' ? 'social' : 'outbound');

  for (const px of pixels) {
    switch (px.provider) {
      case 'fb': {
        // trackSingle fires only to the named pixel even when fbq has more
        // pixels registered from other artists.
        if (typeof w.fbq !== 'function') break;
        safeCall(() => {
          if (eventType === 'page_view') {
            w.fbq('trackSingle', px.pixel_id, 'PageView');
          } else {
            w.fbq('trackSingle', px.pixel_id, 'Lead', {
              content_name: label,
              content_category: category,
              ...(payload.value != null ? { value: payload.value, currency: 'USD' } : {}),
            });
          }
        }, `fb:${px.pixel_id}`);
        break;
      }

      case 'google': {
        // send_to scopes the event to a specific GA property. Without it,
        // gtag('event', ...) fires to every property registered with
        // gtag('config', ...).
        if (typeof w.gtag !== 'function') break;
        safeCall(() => {
          if (eventType === 'page_view') {
            // gtag('config', id) auto-fires page_view; calling 'config' again
            // with the same id updates page_path for SPA navigations.
            w.gtag('config', px.pixel_id, { page_path: typeof location !== 'undefined' ? location.pathname : undefined });
          } else {
            w.gtag('event', 'click', {
              send_to: px.pixel_id,
              event_category: category,
              event_label: label,
              link_url: payload.url,
              ...(payload.value != null ? { value: payload.value } : {}),
            });
          }
        }, `gtag:${px.pixel_id}`);
        break;
      }

      case 'tiktok': {
        // ttq.instance(PIXEL_ID) returns a handle that fires only to that
        // pixel. Falls back to ttq directly if the instance helper is
        // missing (older snippets).
        if (!w.ttq) break;
        safeCall(() => {
          const inst = (typeof w.ttq.instance === 'function')
            ? w.ttq.instance(px.pixel_id)
            : w.ttq;
          if (eventType === 'page_view') {
            inst.page?.();
          } else {
            inst.track?.('ClickButton', {
              content_id: payload.blockId || label,
              content_name: label,
              content_type: category,
            });
          }
        }, `tiktok:${px.pixel_id}`);
        break;
      }

      case 'gtm': {
        // GTM containers read from a shared dataLayer by default. We load
        // each artist's container with a unique dataLayer name (see
        // ArtistPage's GTM init), so pushing to that specific dataLayer
        // means other artists' containers never see the event.
        const dlName = gtmDataLayerName(px.pixel_id);
        const dl = w[dlName] || w.dataLayer;
        if (!Array.isArray(dl)) break;
        safeCall(() => {
          dl.push({
            event: eventType,
            linktree_page_id: payload.pageId,
            linktree_pixel_id: px.pixel_id,
            link_url: payload.url,
            link_name: label,
            link_category: category,
            ...(payload.value != null ? { link_value: payload.value } : {}),
          });
        }, `gtm:${px.pixel_id}`);
        break;
      }
    }
  }
}
